/**
 * What can be done to an account, from its row and from its detail header alike.
 *
 * An account is not edited by one form — it is edited by four acts with four
 * shapes and four permissions — and that used to be the argument for the row
 * offering none of them: a lone pencil labelled "Edit" claims to be the whole
 * edit surface, and the person who clicks it never learns the other three
 * exist.
 *
 * The answer is not to hide them but to offer **all** of them, in both places,
 * from one declaration. A row that carries edit, set password and deactivate
 * makes no false claim, because it is the same set the detail header shows.
 *
 * What is still not here: **memberships**. Groups and roles reach a user
 * through their own add/remove endpoints against a list, and a list is a panel,
 * not an action — there is nothing a single button on a row could do with it.
 * Nothing deletes, either: accounts are deactivated, because audit rows have to
 * keep naming people. See `../people/README.md`.
 *
 * **Impersonate** ends with a full page load, not a navigation: the server has
 * replaced the session cookie, and everything this page cached belongs to the
 * administrator. `Header` does the same on the way back.
 */
import { useTranslation } from 'react-i18next';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import KeyIcon from '@mui/icons-material/Key';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import LockResetIcon from '@mui/icons-material/LockReset';
import SwitchAccountIcon from '@mui/icons-material/SwitchAccount';
import Alert from '@mui/material/Alert';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import type { UserDto } from '@forsys/shared';
import {
  activateUser,
  deactivateUser,
  impersonateUser,
  setPasswordChangeRequired,
  setUserPassword,
  updateUser,
} from '../../api/identity';
import { useAuth } from '../../auth/useAuth';
import {
  formAction,
  useEntityActions,
  type EntityActionsState,
} from '../../hooks/useEntityActions';

interface ProfileForm {
  email: string;
  displayName: string;
}

interface PasswordForm {
  password: string;
  /** On by default: a password an administrator types is usually a temporary one. */
  requireChange: boolean;
}

export interface UseUserActionsOptions {
  /** After any of them succeeds. Reload the list, or hand the record over. */
  onChanged: (user: UserDto | null, key: string) => void;
}

export function useUserActions(options: UseUserActionsOptions): EntityActionsState<UserDto> {
  const { t } = useTranslation('people');
  const { user: self, impersonator } = useAuth();

  return useEntityActions<UserDto>(
    [
      formAction<UserDto, ProfileForm>({
        kind: 'form',
        key: 'edit',
        label: t('userDetail.edit'),
        icon: <EditOutlinedIcon fontSize="small" />,
        permission: 'user.update',
        blank: { email: '', displayName: '' },
        formFor: (user) => ({ email: user.email, displayName: user.displayName }),
        title: () => t('userDetail.editDialog.title'),
        incomplete: (form) => form.email.trim() === '' || form.displayName.trim() === '',
        fields: (form, patch) => (
          <>
            <TextField
              label={t('userDetail.editDialog.email')}
              type="email"
              value={form.email}
              onChange={(event) => patch({ email: event.target.value })}
              helperText={t('userDetail.editDialog.emailHelp')}
              fullWidth
            />
            <TextField
              label={t('userDetail.editDialog.displayName')}
              value={form.displayName}
              onChange={(event) => patch({ displayName: event.target.value })}
              fullWidth
            />
          </>
        ),
        run: (user, form) =>
          updateUser(user.id, {
            email: form.email.trim(),
            displayName: form.displayName.trim(),
          }),
      }),

      formAction<UserDto, PasswordForm>({
        kind: 'form',
        key: 'password',
        label: t('userDetail.setPassword'),
        icon: <KeyIcon fontSize="small" />,
        permission: 'user.set_password',
        blank: { password: '', requireChange: true },
        // Never prefilled from the record: a password is write-only and the
        // server has nothing to hand back. `formFor` starting empty is the
        // point, not an omission.
        formFor: () => ({ password: '', requireChange: true }),
        maxWidth: 'xs',
        title: (user) => t('userDetail.passwordDialog.title', { name: user.displayName }),
        submitLabel: t('userDetail.passwordDialog.submit'),
        incomplete: (form) => form.password.length === 0,
        fields: (form, patch) => (
          <>
            <Alert severity="warning">{t('userDetail.passwordDialog.warning')}</Alert>
            <TextField
              label={t('userDetail.passwordDialog.newPassword')}
              type="password"
              value={form.password}
              onChange={(event) => patch({ password: event.target.value })}
              autoComplete="new-password"
              autoFocus
              fullWidth
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.requireChange}
                  onChange={(event) => patch({ requireChange: event.target.checked })}
                />
              }
              label={t('userDetail.passwordDialog.requireChange')}
            />
          </>
        ),
        run: (user, form) =>
          setUserPassword(user.id, { password: form.password, requireChange: form.requireChange }),
      }),

      {
        // Reversible both ways, so `immediate`, like reactivating.
        kind: 'immediate',
        key: 'requirePasswordChange',
        label: t('userDetail.requirePasswordChange'),
        icon: <LockResetIcon fontSize="small" />,
        permission: 'user.set_password',
        applies: (user) => !user.mustChangePassword,
        run: (user) => setPasswordChangeRequired(user.id, { required: true }),
      },

      {
        kind: 'immediate',
        key: 'cancelPasswordChange',
        label: t('userDetail.cancelPasswordChange'),
        icon: <LockOpenOutlinedIcon fontSize="small" />,
        permission: 'user.set_password',
        applies: (user) => user.mustChangePassword,
        run: (user) => setPasswordChangeRequired(user.id, { required: false }),
      },

      {
        kind: 'confirm',
        key: 'impersonate',
        label: t('userDetail.impersonate'),
        icon: <SwitchAccountIcon fontSize="small" />,
        permission: 'user.impersonate',
        // Not yourself, not a deactivated account, and not from inside another
        // impersonation — the server refuses all three. The superadmin is
        // refused too, but nothing here says who that is.
        applies: (user) => user.isActive && user.id !== self?.id && impersonator === null,
        title: (user) => t('userDetail.impersonateDialog.title', { name: user.displayName }),
        message: t('userDetail.impersonateDialog.message'),
        confirmLabel: t('userDetail.impersonateDialog.confirm'),
        run: async (user) => {
          await impersonateUser(user.id);
          window.location.assign('/');
        },
      },

      {
        kind: 'confirm',
        key: 'deactivate',
        label: t('userDetail.deactivate'),
        icon: <BlockIcon fontSize="small" />,
        permission: 'user.deactivate',
        applies: (user) => user.isActive,
        tone: 'error',
        title: (user) => t('userDetail.deactivateDialog.title', { name: user.displayName }),
        message: t('userDetail.deactivateDialog.message'),
        confirmLabel: t('userDetail.deactivateDialog.confirm'),
        run: (user) => deactivateUser(user.id),
      },

      {
        // Reactivating is `immediate`, with no confirmation. A confirmation on a
        // reversible act trains people to click through confirmations — which is
        // how the one that mattered, the deactivate above, gets clicked through.
        kind: 'immediate',
        key: 'reactivate',
        label: t('userDetail.reactivate'),
        icon: <CheckCircleOutlinedIcon fontSize="small" />,
        permission: 'user.deactivate',
        applies: (user) => !user.isActive,
        run: (user) => activateUser(user.id),
      },
    ],
    {
      onChanged: (result, key) => {
        // Setting a password answers `{ user }` too, but the record did not
        // change — only the fact did. The key tells the screen which happened,
        // so it can say "password changed" without re-rendering the account.
        const user = (result as { user?: UserDto } | undefined)?.user ?? null;
        options.onChanged(key === 'password' ? null : user, key);
      },
    },
  );
}
